// Enumerating a program's outcomes, at transaction granularity.
//
// This is PR 4a of docs/design/txn-granular-exploration.md — the explorer the
// conflict primitive was built for. The timeline tree answers "what else could
// this program have done?" by **sampling**: force a schedule prefix, let a
// different tie-break finish the run, and union what comes back. That is why its
// badge reads `≥`. This module answers the same question by **enumeration**, and
// when it finishes without hitting its budget the answer is `=`: these outcomes
// and no others.
//
// **Why enumeration is possible at all.** The step-level tree is hopeless —
// every memory access is an interleaving point, so `lost_update.stm` alone has
// some ninety thousand leaves, nearly all of them reorderings of independent
// accesses that land in the same place. Three observations cut it to something
// walkable, and each is a claim about the language rather than a heuristic:
//
//   1. **A step that emits nothing observable commutes with everything.** Half
//      of all steps are a thread resuming and immediately pausing again. Nothing
//      an outcome is made of (a read, a write, a print, an assertion) happens
//      there, so the order it takes relative to another thread cannot show up in
//      an ending — and every later ordering is still decided later.
//   2. **A committed transaction is atomic**, so the interleavings *inside* one
//      need not be explored: any of them is equivalent to some execution where
//      the transaction ran alone (that is what `validate()` enforces — conflict
//      serializability). The abort-and-retry those interleavings produce is the
//      STM resolving the conflict, not a different outcome. So the decisions are
//      exactly the **unit boundaries** of `conflict.ts`: which transaction, or
//      which bare access, goes next.
//   3. **Only a step with more than one runnable thread is a decision.** The
//      rest is the program, not the scheduler.
//   4. **Constructing an object commutes with everything.** A step that only
//      touches addresses nothing has touched before is building something no
//      other thread can name yet — an object it has no reference to — so no
//      transition enabled anywhere else can conflict with it, and it may be
//      taken without branching. This is not a micro-optimization: `futures`
//      spends 7 of its 8 unit boundaries on `spawn()` allocating a `Future` and
//      on dict/set literals, and `Chan`'s iterator is allocated the same way.
//
// What is left is a tree over unit orderings, and on the fixtures it is tiny:
// `counter` and `bank` have **no** decision at all — every observable step is
// inside a transaction — so one run proves their single outcome. `lost_update`,
// which uses no `txn`, takes 22 runs to prove exactly its famous two.
//
// **When the program asserts something, the walk has a goal.** An assertion is
// the author saying what should be true however the threads interleave, so the
// question stops being "what is the whole outcome set" and becomes "is there an
// ordering that breaks it?" — and the moment the walk has one schedule where the
// assertions hold and one where they fail, it has the answer and stops. That is
// a *race*, named and reproducible, in 6 runs on `lost_update.stm` rather than
// 22. The other verdict is just as useful and costs the full walk: when the
// enumeration finishes having seen only passes, the assertion holds under *every*
// ordering — which is what `bank.stm` and `counter.stm` are for.
//
// **What "complete" does and does not mean.** It means: for this seed, these
// options, and this step budget, no other outcome is reachable — under
// observation (2), which is a claim about *committed* transactions. Two things
// break it, and both are reported rather than assumed away: a run that dies on
// its step budget leaves a subtree unexplored, and an `assert` *inside* a
// transaction is re-recorded by a retry, so the interleavings inside that
// transaction are observable after all. When either shows up, the result says
// `complete: false` and why. Being wrong about this is worse than being vague:
// the whole point of `=` is that it can be trusted. (IO cannot do this: the
// effect system already refuses `print` inside a `txn`, "it performs IO, and a
// transaction may retry". `assert` is not IO and is not refused, so it is the
// one effect a retry can repeat — and the one this has to watch for.)

import { run } from "../src/compiler.js";
import type { RunResult } from "../src/compiler.js";
import type { Causality } from "../src/backend/interp/causality.js";
import { txnUnits } from "./conflict.js";
import type { Units } from "./conflict.js";
import type { RunParams } from "./race-oracle.js";
import { endingLabel, endingOf, picksBefore } from "./timeline.js";
import type { AssertResult, Ending } from "./timeline.js";

/** One outcome, with a schedule that reaches it. */
export interface Outcome {
  /** Canonical identity — assertions, prints, and the final heap up to address
   *  renaming (`outcomeKey`). */
  key: string;
  /** What the timeline tree would put on a node's badge. */
  label: string;
  ending: Ending;
  /** The schedule prefix that produced it, so the playground can open this
   *  timeline rather than merely name it. Empty for the default run. */
  schedule: number[];
}

/** Why an enumeration is only a lower bound. */
export type Incomplete =
  /** It stopped on purpose: the program asserts something, and both verdicts had
   *  been seen. What was being looked for was found. */
  | "race-found"
  /** The step budget ran out with decisions still on the stack. */
  | "budget"
  /** An `assert` ran inside a transaction, so a retry re-emits it and the
   *  interleavings inside that transaction are observable after all. */
  | "effects-in-txn"
  /** A schedule pushed the program past its step or time limit, leaving that
   *  subtree unexplored. */
  | "run-failed";

/** What a thread does when you let it in here: one unit, named. Structured
 *  rather than rendered, because the address a caller wants to *show* depends on
 *  the object types it holds, and the worker has no idea what those are. */
export interface UnitDid {
  /** `txn` for a committed transaction, otherwise the access or the effect. */
  op: "txn" | "read" | "write" | "print" | "assert";
  /** The transaction's id, when this unit is one. */
  txn: number | null;
  /** The unit's footprint (a transaction) or the address it touched (a bare
   *  access). Empty for a print or an assertion. */
  addrs: string[];
}

/**
 * One node of the walk: a schedule prefix, and the run that reaches it.
 *
 * The tree the enumeration walks *is* a tree of timelines — one node per
 * decision about which unit goes next — so it is kept rather than thrown away
 * once the outcomes are counted. Nodes are ordered by discovery, breadth first,
 * and `id` is the index in `ExploreTree.nodes`; the root is 0.
 */
export interface ExploreNode {
  id: number;
  /** The node this one departs from; null at the root. */
  parent: number | null;
  /** The schedule that reaches it: thread picks from step 0. Forcing this and
   *  letting the heuristic finish *is* this node's run. */
  prefix: number[];
  /** The thread let in here; null at the root, which decided nothing. */
  thread: number | null;
  /** What that thread does with its turn. Null when it does nothing visible. */
  did: UnitDid | null;
  /** Where this node's own run ended — the timeline you get by taking this
   *  decision and letting the scheduler finish. `asserts` is the same thing
   *  unrendered, so a set of these can be read back as "which claims, and which
   *  verdicts" rather than as a list of strings that happen to share a prefix. */
  outcome: { key: string; label: string; asserts: AssertResult[] };
  /** What that run's assertions came to, so a node can be marked as a witness. */
  verdict: Verdict;
  /** Discovered children, in tie-break order, so `children[0]` is the pick the
   *  unforced heuristic makes here. */
  children: number[];
  /** Nothing left to decide below: this run is the only way on from here, so
   *  its outcome is the *only* outcome, exactly. */
  settled: boolean;
  /** The node whose position this one repeats — same committed heap, same
   *  progress per thread, reached another way. Its subtree is that node's, so
   *  it is not walked twice. */
  repeatOf: number | null;
}

/** What a run's assertions came to: `none` when the program asserts nothing. */
export type Verdict = "pass" | "fail" | "none";

/** An assertion that holds under one ordering and fails under another, with a
 *  schedule for each — the thing a reader of `lost_update.stm` is looking for. */
export interface Race {
  passes: Outcome;
  fails: Outcome;
}

export interface ExploreResult {
  /** Every outcome found, in first-seen order. */
  outcomes: Outcome[];
  /** True when the enumeration finished — then `outcomes` is exactly the
   *  reachable set for these run parameters, and the panel may say `=`. */
  complete: boolean;
  /** Why not, when not — in the order encountered. */
  incomplete: Incomplete[];
  /** Executions run. One per node visited; the cost of the answer. */
  runs: number;
  /** Unit boundaries where more than one thread could have gone next — the
   *  decision space the enumeration actually walked. */
  decisions: number;
  /** Units in the default run (committed transactions + bare accesses), for
   *  scale: this is what the step-level tree's forks collapse to. */
  units: number;
  /** The tree the walk went through, breadth first, root at 0. */
  nodes: ExploreNode[];
  /** The program's assertions across the orderings walked: `pass` when every one
   *  of them held, `fail` when any ordering broke them, `none` when the program
   *  asserts nothing. A `fail` with `race` set is a claim that is *sometimes*
   *  true — the interesting case; a `fail` without one fails every way. */
  verdict: Verdict;
  /** Both verdicts, with a schedule that reaches each — the assertion is racy.
   *  Null when it never split (or the program asserts nothing). */
  race: Race | null;
}

/**
 * The final heap, named so that *which* address an object got cannot count as an
 * outcome.
 *
 * Addresses are handed out in allocation order, so a schedule that lets one
 * thread allocate before another renames every object after it — and a heap
 * compared address-by-address then reads as a different ending. It is not one:
 * `channels` and `game` each came back with "two outcomes" that were the same
 * world with two objects swapped. (`channels`' pair even printed the same line,
 * so the panel would have shown `= { sum of squares 30, sum of squares 30 }`.)
 *
 * So each address is renamed by *what it holds* rather than when it was made:
 * sort the entries by their rendered value, name them `#0`, `#1`, … in that
 * order, rewrite the references inside the values with those names, and repeat —
 * three passes, which is enough for a reference to be named by what it points at
 * rather than by where it landed. Two heaps that are the same up to renaming
 * canonicalize identically; two that differ in any value still differ.
 */
export function canonicalFinal(final: ReadonlyMap<string, string>): string[] {
  const addrs = [...final.keys()].sort((a, b) => b.length - a.length || (a < b ? -1 : 1));
  let names = new Map<string, string>(addrs.map((a) => [a, a]));
  let rewritten: Array<[string, string]> = [];
  for (let pass = 0; pass < 3; pass++) {
    rewritten = [...final].map(([addr, value]) => {
      let out = value;
      // Longest address first, so `/1` cannot rewrite the front of `/10`.
      for (const a of addrs) out = out.split(a).join(names.get(a)!);
      return [addr, out];
    });
    const order = [...rewritten].sort((x, y) => (x[1] < y[1] ? -1 : x[1] > y[1] ? 1 : x[0] < y[0] ? -1 : 1));
    names = new Map(order.map(([addr], i) => [addr, `#${i}`]));
  }
  return rewritten.map(([addr, value]) => `${names.get(addr)}=${value}`).sort();
}

/**
 * What a run's assertions came to.
 *
 * One failure is a failure: an `assert` is a claim about every ordering, so a
 * run that breaks any of them is the counter-example, whatever else it passed.
 */
export function verdictOf(e: Ending): Verdict {
  if (e.asserts.length === 0) return "none";
  return e.asserts.some((a) => !a.passed) ? "fail" : "pass";
}

/** What makes two endings the same outcome: every assertion's verdict,
 *  everything printed, and the final heap up to address renaming. */
export function outcomeKey(e: Ending): string {
  return JSON.stringify({ asserts: e.asserts, prints: e.prints, final: canonicalFinal(e.final) });
}

/** How many executions an enumeration may run before giving up and admitting it
 *  is a lower bound. Generous: a node is one run of a small program, and the
 *  fixtures finish in tens. */
export const DEFAULT_BUDGET = 2000;

/** How the enumeration is going, for a caller that wants to say so while it
 *  waits. Reported every `PROGRESS_EVERY` runs. */
export interface Progress {
  /** Executions run so far. */
  runs: number;
  /** Distinct outcomes found so far — a lower bound until it finishes. */
  outcomes: number;
  /** Nodes still to visit; zero means the next report is the last. */
  frontier: number;
}

/** Runs between progress reports. Small enough that a slow program still moves,
 *  large enough that reporting is not the work. */
const PROGRESS_EVERY = 8;

export interface ExploreOptions {
  /** Executions to allow before reporting a lower bound (`DEFAULT_BUDGET`). */
  budget?: number;
  /** Walk everything, even once an assertion has been seen to both hold and
   *  fail. The early stop is the right default — the race *is* the answer — but
   *  "and what else can this program do?" is a fair next question. */
  exhaustive?: boolean;
  /** Called every few runs while the walk proceeds, with the nodes found since
   *  the last call — so a caller drawing the tree can draw it as it grows
   *  rather than after it is finished. */
  onProgress?: (p: Progress, nodes: ExploreNode[]) => void;
}

/** The world just before step `k`: which threads are inside a transaction, and
 *  the committed heap. Both are read off the events the run emitted before that
 *  step — a transaction's writes join the heap at its commit and are dropped on
 *  an abort, which is what makes this the *committed* state. */
function stateBefore(r: RunResult, k: number): { open: Map<number, number>; sig: string } {
  const c = r.causality!;
  const end = k < c.steps.length ? c.steps[k]!.firstEvent : r.trace.events.length;
  const open = new Map<number, number>();
  const heap = new Map<string, string>();
  const pending = new Map<number, Array<[string, string]>>();
  for (let i = 0; i < end; i++) {
    const ev = r.trace.events[i]!;
    switch (ev.type) {
      case "txn-begin":
        open.set(ev.thread, ev.txn);
        break;
      case "txn-commit":
        open.delete(ev.thread);
        for (const [addr, value] of pending.get(ev.txn) ?? []) heap.set(addr, value);
        pending.delete(ev.txn);
        break;
      case "txn-abort":
      case "txn-retry":
        open.delete(ev.thread);
        pending.delete(ev.txn); // rolled back — never happened
        break;
      case "write":
        if (ev.txn === "auto") heap.set(String(ev.addr), ev.value);
        else pending.set(ev.txn, [...(pending.get(ev.txn) ?? []), [String(ev.addr), ev.value]]);
        break;
      default:
        break;
    }
  }
  // Two nodes with the same committed heap and the same per-thread progress are
  // the same position reached two ways, so the second need not be expanded.
  const steps = new Map<number, number>();
  for (let i = 0; i < k; i++) {
    const s = c.steps[i]!;
    steps.set(s.thread, (steps.get(s.thread) ?? 0) + 1);
  }
  const sig = JSON.stringify([[...heap].sort(), [...steps].sort(), [...open].sort()]);
  return { open, sig };
}

/** Whether this step emits anything an outcome is made of. */
function observable(r: RunResult, step: number): boolean {
  const s = r.causality!.steps[step]!;
  for (let row = s.firstEvent; row < s.endEvent; row++) {
    const t = r.trace.events[row]!.type;
    if (t === "read" || t === "write" || t === "print" || t === "assert") return true;
  }
  return false;
}

/** The row each address was first touched at, so a step can tell whether it is
 *  building something or using something. */
function firstTouch(r: RunResult): Map<string, number> {
  const out = new Map<string, number>();
  r.trace.events.forEach((ev, row) => {
    if (ev.type !== "read" && ev.type !== "write") return;
    const addr = String(ev.addr);
    if (!out.has(addr)) out.set(addr, row);
  });
  return out;
}

/**
 * Whether this step is *construction*: every address it touches is one nothing
 * has touched before, and it says nothing.
 *
 * Such a step commutes with every transition enabled anywhere else, because no
 * other thread holds a reference to an object that does not exist yet — the only
 * way to get one is through this thread, and this thread is the one running. So
 * it is not a decision: taking it now is equivalent to taking it later.
 *
 * This is where the store being addressable bites. `f = spawn(...)` writes a
 * `Future`, a dict literal writes the dict and every entry, `for v in ch` writes
 * an iterator — all of them memory operations outside any transaction, so all of
 * them unit boundaries, and none of them a choice about anything.
 */
function construction(r: RunResult, step: number, first: ReadonlyMap<string, number>): boolean {
  const s = r.causality!.steps[step]!;
  for (let row = s.firstEvent; row < s.endEvent; row++) {
    const ev = r.trace.events[row]!;
    if (ev.type === "print" || ev.type === "assert") return false;
    if (ev.type !== "read" && ev.type !== "write") continue;
    if ((first.get(String(ev.addr)) ?? -1) < s.firstEvent) return false; // touched before: not new
  }
  return true;
}

/**
 * Whether an `assert` ran inside a transaction.
 *
 * The one thing that breaks the atomicity observation: an aborted attempt rolls
 * its *writes* back, but a verdict it already recorded stays in the trace, and
 * the retry records another. Where that happens the interleavings inside a
 * transaction are observable — one schedule asserts twice, another three times —
 * and skipping them would be a lie, so the enumeration says so instead.
 *
 * `print` cannot do this: the effect system refuses IO inside a `txn` precisely
 * because a transaction may retry. `assert` is not IO, so it is not refused.
 */
function effectsInTxn(r: RunResult): boolean {
  const inTxn = new Set<number>();
  for (const ev of r.trace.events) {
    if (ev.type === "txn-begin") inTxn.add(ev.thread);
    else if (ev.type === "txn-commit" || ev.type === "txn-abort" || ev.type === "txn-retry") inTxn.delete(ev.thread);
    else if ((ev.type === "print" || ev.type === "assert") && inTxn.has(ev.thread)) return true;
  }
  return false;
}

/**
 * What `thread` does with the turn it was given here: the unit it runs.
 *
 * A node's label is not "this step" but "this decision" — which is a whole
 * transaction when the thread is about to run one, and a single access when it
 * is not. Looking forward from the step rather than at it, because a thread's
 * turn often begins with a step that emits nothing.
 */
function unitDid(r: RunResult, step: number, thread: number, units: Units): UnitDid | null {
  const c = r.causality!;
  for (let i = step; i < c.steps.length; i++) {
    const s = c.steps[i]!;
    if (s.thread !== thread) continue;
    for (let row = s.firstEvent; row < s.endEvent; row++) {
      const ev = r.trace.events[row]!;
      if (ev.type === "read" || ev.type === "write") {
        const u = units.unitOfRow.get(row);
        if (u && u.kind === "txn") {
          return { op: "txn", txn: u.txn, addrs: [...new Set([...u.reads, ...u.writes])].sort() };
        }
        return { op: ev.type, txn: null, addrs: [String(ev.addr)] };
      }
      if (ev.type === "print" || ev.type === "assert") return { op: ev.type, txn: null, addrs: [] };
    }
  }
  return null;
}

/**
 * Enumerate the outcomes reachable from a program, deciding only unit orderings.
 *
 * One run per node: the schedule prefix is forced and the ordinary heuristic
 * finishes the execution, so every node is a complete run with an ending of its
 * own — the same trick the timeline tree uses, with the difference that the nodes
 * here are unit boundaries rather than steps, and that the walk terminates.
 *
 * `params` are the run's own (seed, time limit, strategy). The seed still picks
 * the tie-break the *unforced* part of each run takes, so it can change which
 * schedule realizes an outcome; what it cannot change, when the result is
 * complete, is the set.
 */
export function exploreOutcomes(
  source: string,
  params: RunParams,
  options: ExploreOptions = {}
): ExploreResult {
  const { budget = DEFAULT_BUDGET, exhaustive = false, onProgress } = options;
  const outcomes = new Map<string, Outcome>();
  const seen = new Map<string, number>(); // state signature -> the node that owns it
  const incomplete: Incomplete[] = [];
  const note = (why: Incomplete): void => {
    if (!incomplete.includes(why)) incomplete.push(why);
  };

  const nodes: ExploreNode[] = [];
  let sent = 0; // nodes already handed to `onProgress`
  let runs = 0;
  let decisions = 0;
  let units = 0;
  let atomic = true; // until an assert turns up inside a transaction
  // The two witnesses, once the walk has them: an ordering whose assertions hold
  // and one where they fail. Finding both is the point of walking at all when a
  // program asserts anything, so it is also where the walk stops.
  const witness = new Map<Verdict, Outcome>();

  /** The frontier, oldest first. **Breadth first on purpose**: a walk that runs
   *  out of budget should leave a tree that is complete near the root and cut
   *  off at the bottom, which is a tree you can still read, rather than one deep
   *  spike into a corner of the schedule space. */
  const queue: Array<{ prefix: number[]; parent: number | null }> = [{ prefix: [], parent: null }];
  while (queue.length > 0) {
    if (runs >= budget) {
      note("budget");
      break;
    }
    const { prefix, parent } = queue.shift()!;
    runs++;
    if (onProgress && runs % PROGRESS_EVERY === 0) {
      onProgress({ runs, outcomes: outcomes.size, frontier: queue.length }, nodes.slice(sent));
      sent = nodes.length;
    }
    let r: RunResult;
    try {
      r = run(source, { ...params, causality: true, schedule: { from: 0, threads: prefix } });
    } catch (e) {
      // The very first run is the program itself. If *that* fails there is
      // nothing to enumerate and no lower bound to report either — an empty set
      // labelled "incomplete" would read as "no outcomes found", which is a
      // different and much more misleading claim than "this does not run".
      if (prefix.length === 0) throw e;
      note("run-failed"); // past its step or time budget: this subtree is unwalked
      continue;
    }
    // The override asked for a thread that wasn't runnable — not an execution the
    // interpreter could have produced, so there is nothing here to miss.
    if (r.scheduleAbandoned) continue;
    const c = r.causality;
    if (!c) throw new Error("exploreOutcomes needs a causality-bearing run");

    const walked = txnUnits(r.trace);
    if (prefix.length === 0) {
      units = walked.units.length;
      if (effectsInTxn(r)) {
        atomic = false;
        note("effects-in-txn");
      }
    }

    const first = firstTouch(r);
    const ending = endingOf(r);
    const key = outcomeKey(ending);
    const label = endingLabel(r);
    const outcome = outcomes.get(key) ?? { key, label, ending, schedule: prefix };
    if (!outcomes.has(key)) outcomes.set(key, outcome);
    const verdict = verdictOf(ending);
    if (verdict !== "none" && !witness.has(verdict)) witness.set(verdict, outcome);

    const thread = prefix.length > 0 ? prefix[prefix.length - 1]! : null;
    const node: ExploreNode = {
      id: nodes.length,
      parent,
      prefix,
      thread,
      did: thread === null ? null : unitDid(r, prefix.length - 1, thread, walked),
      outcome: { key, label, asserts: ending.asserts },
      verdict,
      children: [],
      settled: false,
      repeatOf: null,
    };
    nodes.push(node);
    if (parent !== null) nodes[parent]!.children.push(node.id);

    // Both verdicts in hand: the assertion is racy, here is the ordering that
    // breaks it and the one that doesn't, and nothing further down can make that
    // more true. Walking on would only lengthen a list of outcomes nobody asked
    // for — the question was whether the claim holds, and it does not.
    if (witness.size === 2 && !exhaustive) {
      note("race-found");
      break;
    }

    // The next unit boundary where more than one thread could go next.
    let at = -1;
    let choices: number[] = [];
    for (let i = prefix.length; i < c.steps.length; i++) {
      const s = c.steps[i]!;
      if (s.choices.length < 2) continue; // forced by the program, not chosen
      if (!observable(r, i)) continue; // nothing an outcome is made of happens here
      if (construction(r, i, first)) continue; // building what no one else can name yet
      if (atomic) {
        const { open } = stateBefore(r, i);
        // Inside a transaction: the language already says this cannot be split.
        if ([...open.keys()].some((t) => s.choices.includes(t))) continue;
      }
      at = i;
      choices = s.choices;
      break;
    }
    if (at < 0) {
      node.settled = true; // nothing left to decide — this run is the only way on
      continue;
    }

    decisions++;
    const { sig } = stateBefore(r, at);
    const owner = seen.get(sig);
    if (owner !== undefined) {
      // The same position, reached another way: same committed heap, same
      // progress per thread. Whatever is reachable below it is reachable below
      // that node, and walking it again would only redraw the same subtree.
      node.repeatOf = owner;
      continue;
    }
    seen.set(sig, node.id);
    const before = picksBefore(c, at);
    for (const t of choices) queue.push({ prefix: [...before, t], parent: node.id });
  }

  if (onProgress && nodes.length > sent) {
    onProgress({ runs, outcomes: outcomes.size, frontier: queue.length }, nodes.slice(sent));
  }

  const passes = witness.get("pass");
  const fails = witness.get("fail");
  return {
    outcomes: [...outcomes.values()],
    complete: incomplete.length === 0,
    incomplete,
    runs,
    decisions,
    units,
    nodes,
    verdict: passes && fails ? "fail" : (fails ? "fail" : passes ? "pass" : "none"),
    race: passes && fails ? { passes, fails } : null,
  };
}

/**
 * One assertion, and every verdict it took across a set of outcomes.
 *
 * A set of endings is not really a list of strings — it is a *claim* and the
 * verdicts that claim took: `{no increment lost: {✓, ✗}}` says one thing where
 * `{✓ no increment lost, ✗ no increment lost}` says it twice. Programs with two
 * assertions produce two entries, so the one that actually splits is the one you
 * see split.
 */
export interface ClaimVerdicts {
  message: string;
  /** `pass` first when both are present — the order the set is drawn in. */
  verdicts: Array<"pass" | "fail">;
}

/** A set of outcomes, said once: the claims and their verdicts, plus whatever
 *  carried no assertion at all (a `print` line — the ending of a program that
 *  states nothing, which has no verdicts to fold). */
export interface OutcomeSummary {
  claims: ClaimVerdicts[];
  plain: string[];
}

/** Index a walk's outcomes by the label they render as, so a node's set of
 *  labels can be read back as the assertions behind it. Every label with
 *  assertions *is* their rendering (`assertLabel`), so this is a function. */
export function assertIndex(nodes: readonly ExploreNode[]): Map<string, AssertResult[]> {
  const out = new Map<string, AssertResult[]>();
  for (const node of nodes) {
    if (node.outcome.asserts.length > 0 && !out.has(node.outcome.label)) {
      out.set(node.outcome.label, node.outcome.asserts);
    }
  }
  return out;
}

/** Fold a set of ending labels into the claims behind them. */
export function summarize(labels: readonly string[], index: ReadonlyMap<string, AssertResult[]>): OutcomeSummary {
  const claims = new Map<string, Set<"pass" | "fail">>();
  const plain: string[] = [];
  for (const label of labels) {
    const asserts = index.get(label);
    if (!asserts) {
      // No assertion behind it: a print, or a program that said nothing.
      if (label !== "" && !plain.includes(label)) plain.push(label);
      continue;
    }
    for (const a of asserts) {
      const seen = claims.get(a.message) ?? new Set<"pass" | "fail">();
      seen.add(a.passed ? "pass" : "fail");
      claims.set(a.message, seen);
    }
  }
  return {
    claims: [...claims].map(([message, seen]) => ({
      message,
      verdicts: (["pass", "fail"] as const).filter((v) => seen.has(v)),
    })),
    plain,
  };
}

/** What a node's subtree can end in, and whether that is the whole of it. */
export interface NodeOutcomes {
  /** Ending labels reachable below, sorted, deduplicated. */
  outcomes: string[];
  /** True when nothing else is reachable from here — the node may say `=`. */
  exact: boolean;
}

/**
 * Resolve every node's outcome set, bottom up.
 *
 * The whole point of enumerating rather than sampling: a node's set is the union
 * of its children's, a settled leaf's set is the one ending it has, and a node
 * that repeats a position borrows the set of the node it repeats. No seeds, no
 * floor — except where the walk was cut off, and there the node says so.
 *
 * Pure, and defined over *any* prefix of the walk, so a caller drawing the tree
 * as it streams in can call it on what it has: unexpanded nodes are simply not
 * exact yet, which is exactly what `≥` means.
 */
export function resolveOutcomes(nodes: readonly ExploreNode[]): Map<number, NodeOutcomes> {
  const out = new Map<number, NodeOutcomes>();
  const visiting = new Set<number>();

  const resolve = (id: number): NodeOutcomes => {
    const cached = out.get(id);
    if (cached) return cached;
    const node = nodes[id];
    if (!node) return { outcomes: [], exact: false };
    if (visiting.has(id)) return { outcomes: [node.outcome.label], exact: false }; // never happens: the walk cannot loop
    visiting.add(id);
    let answer: NodeOutcomes;
    if (node.settled) {
      answer = { outcomes: [node.outcome.label], exact: true };
    } else if (node.repeatOf !== null) {
      answer = resolve(node.repeatOf);
    } else if (node.children.length > 0) {
      const set = new Set<string>();
      let exact = true;
      for (const child of node.children) {
        const below = resolve(child);
        for (const o of below.outcomes) set.add(o);
        exact &&= below.exact;
      }
      answer = { outcomes: [...set].sort(), exact };
    } else {
      // Queued but never walked — the budget ran out, or its own run broke.
      answer = { outcomes: [node.outcome.label], exact: false };
    }
    visiting.delete(id);
    out.set(id, answer);
    return answer;
  };

  for (let id = 0; id < nodes.length; id++) resolve(id);
  return out;
}
